/**
 * MobileAuditsHome — Mobile shell for /audits ( <lg ). Batch 6.3.
 * Renders the same 5 sub-tabs desktop uses (Overview / Records / Schedule /
 * Findings / Templates). All KPI values, scores, action counts and CQC
 * mappings come straight from the server payload — no derivation on mobile.
 */
import React from "react";
import {
  ClipboardCheck, RefreshCw, Plus, MoreVertical,
  Gauge, ClipboardList, CalendarClock, AlertTriangle, LineChart, LayoutTemplate,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import AuditOverviewTab from "@/components/audits/AuditOverviewTab";
import MobileAuditRecordsList from "@/components/mobile/MobileAuditRecordsList";
import MobileAuditFindingsList from "@/components/mobile/MobileAuditFindingsList";
import MobileAuditScheduleList from "@/components/mobile/MobileAuditScheduleList";
import MobileAuditTemplatesList from "@/components/mobile/MobileAuditTemplatesList";

const TABS = [
  { key: "overview",  label: "Overview",  Icon: LineChart },
  { key: "records",   label: "Records",   Icon: ClipboardList },
  { key: "schedule",  label: "Schedule",  Icon: CalendarClock },
  { key: "findings",  label: "Findings",  Icon: AlertTriangle },
  { key: "templates", label: "Templates", Icon: LayoutTemplate },
];

function KpiTile({ label, value, sub, tone = "slate", testid, Icon }) {
  const toneCls = {
    slate:   "border-slate-200 text-slate-900 bg-white",
    emerald: "border-emerald-200 text-emerald-800 bg-emerald-50/60",
    amber:   "border-amber-200 text-amber-800 bg-amber-50/60",
    red:     "border-red-200 text-red-800 bg-red-50/60",
    sky:     "border-sky-200 text-sky-800 bg-sky-50/60",
  }[tone] || "border-slate-200 text-slate-900 bg-white";
  return (
    <div data-testid={testid} className={`shrink-0 w-[150px] rounded-lg border px-3 py-2 ${toneCls}`}>
      <div className="text-[9px] uppercase tracking-widest font-semibold opacity-80 flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />}{label}
      </div>
      <div className="font-heading text-lg font-semibold tabular-nums mt-0.5">{value}</div>
      <div className="text-[10px] opacity-70 truncate">{sub}</div>
    </div>
  );
}

export default function MobileAuditsHome({
  tab, setTab, query, setQuery,
  summary, actions,
  canManage, refreshTick, onRefresh, onNewAudit,
  onOpenAudit, onChanged,
}) {
  const s = summary || {};
  const score = s.overall_score;
  const scoreLabel = score == null ? "—" : `${score.toFixed(1)}%`;
  const scoreTone = score == null ? "slate" : score >= 90 ? "emerald" : score >= 70 ? "amber" : "red";
  const totalActions = actions?.total || 0;

  return (
    <div className="flex-1 min-h-0 flex flex-col bg-slate-50" data-testid="mobile-audits-home">
      {/* Compact header */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 shrink-0">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-sky-50 border border-sky-100 flex items-center justify-center shrink-0">
            <ClipboardCheck className="w-4 h-4 text-sky-700" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Governance</div>
            <h1 className="font-heading text-lg font-semibold text-slate-900 truncate" data-testid="mobile-audits-qa-title">
              Audits &amp; Quality Assurance
            </h1>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {canManage && (
              <Button
                size="sm"
                onClick={onNewAudit}
                data-testid="btn-mobile-audits-qa-new"
                className="h-11 min-h-[44px] bg-sky-700 hover:bg-sky-800 text-xs px-3"
              >
                <Plus className="w-4 h-4 mr-1" /> New
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="btn-mobile-audits-qa-more"
                  className="min-h-[44px] w-11 h-11 flex items-center justify-center rounded-md text-slate-600 hover:bg-slate-100"
                  aria-label="More actions"
                >
                  <MoreVertical className="w-5 h-5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onClick={onRefresh} data-testid="btn-mobile-audits-qa-refresh">
                  <RefreshCw className="w-3.5 h-3.5 mr-2" /> Refresh
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* KPI strip — horizontal snap-scroll */}
        <div
          className="mt-3 -mx-4 px-4 overflow-x-auto snap-x snap-mandatory flex gap-2 pb-1"
          data-testid="mobile-audits-kpi-strip"
        >
          <KpiTile Icon={Gauge} testid="mobile-aq-score" label="Overall Score" value={scoreLabel}
            sub={s.audits_scored ? `Across ${s.audits_scored} audit${s.audits_scored === 1 ? "" : "s"}` : "No scored audits"}
            tone={scoreTone} />
          <KpiTile Icon={ClipboardCheck} testid="mobile-aq-completed" label="Completed 30d" value={summary ? s.completed_this_month : "—"}
            sub="Closed since 1st" tone={summary && s.completed_this_month > 0 ? "emerald" : "slate"} />
          <KpiTile Icon={CalendarClock} testid="mobile-aq-due" label="Due Soon" value={summary ? s.due_soon : "—"}
            sub="Within 30 days" tone={s.due_soon > 0 ? "amber" : "emerald"} />
          <KpiTile Icon={AlertTriangle} testid="mobile-aq-overdue" label="Overdue" value={summary ? s.overdue : "—"}
            sub="Past next-due" tone={s.overdue > 0 ? "red" : "emerald"} />
          <KpiTile Icon={ClipboardList} testid="mobile-aq-actions" label="Open Actions" value={summary ? s.open_actions : "—"}
            sub="Corrective actions" tone={s.open_actions > 0 ? "amber" : "emerald"} />
        </div>
      </div>

      {/* Sub-tab pills */}
      <div className="bg-white border-b border-slate-200 px-4 shrink-0">
        <div className="flex items-center gap-1 overflow-x-auto" data-testid="mobile-audits-tabs">
          {TABS.map((t) => {
            const active = tab === t.key;
            const Icon = t.Icon;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`mobile-audits-qa-tab-${t.key}`}
                className={`min-h-[44px] shrink-0 px-3 py-2 text-xs font-medium border-b-2 flex items-center gap-1.5 whitespace-nowrap transition ${
                  active ? "border-sky-700 text-sky-700" : "border-transparent text-slate-500"
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Attention banner — driven off actions.total from the server */}
      {totalActions > 0 && (
        <div className="px-4 pt-3 shrink-0">
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 flex items-center gap-2" data-testid="mobile-audits-attention">
            <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0" />
            <div className="text-xs text-amber-900 min-w-0">
              <div className="font-medium">{totalActions} audit action{totalActions === 1 ? "" : "s"} require attention</div>
              <div className="text-[11px] text-amber-800/80 mt-0.5">
                {(actions?.overdue_audits?.length || 0) > 0 && <span className="mr-2">{actions.overdue_audits.length} overdue audits</span>}
                {(actions?.overdue_actions?.length || 0) > 0 && <span className="mr-2">· {actions.overdue_actions.length} overdue actions</span>}
                {(actions?.unresolved_significant?.length || 0) > 0 && <span>· {actions.unresolved_significant.length} significant</span>}
              </div>
            </div>
            <button onClick={() => setTab("findings")} data-testid="btn-mobile-audits-attention-view"
              className="ml-auto min-h-[44px] shrink-0 text-xs font-medium text-amber-900 underline">
              View
            </button>
          </div>
        </div>
      )}

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="p-4">
          {tab === "overview" && (
            <AuditOverviewTab summary={summary} actions={actions} onOpenAudit={onOpenAudit} refreshTick={refreshTick} />
          )}
          {tab === "records" && (
            <MobileAuditRecordsList search={query} setSearch={setQuery} onOpenAudit={onOpenAudit} refreshTick={refreshTick} />
          )}
          {tab === "schedule" && (
            <MobileAuditScheduleList canManage={canManage} refreshTick={refreshTick} onChanged={onChanged} />
          )}
          {tab === "findings" && (
            <MobileAuditFindingsList onOpenAudit={onOpenAudit} refreshTick={refreshTick} />
          )}
          {tab === "templates" && (
            <MobileAuditTemplatesList canManage={canManage} refreshTick={refreshTick} onChanged={onChanged} />
          )}
        </div>
      </div>
    </div>
  );
}
